import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';
import { DirectoryPartner } from '../types';

// Convert Firestore timestamp to Date
const toDate = (timestamp: Timestamp | undefined): Date => {
  return timestamp?.toDate() || new Date();
};

// Get all directory partners
export const getDirectoryPartners = async (): Promise<DirectoryPartner[]> => {
  const q = query(
    collection(db, 'retailerDirectory'),
    orderBy('name', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      name: data.name,
      address: data.address,
      city: data.city,
      province: data.province,
      state: data.state,
      country: data.country,
      contactName: data.contactName,
      email: data.email,
      phone: data.phone,
      taxId: data.taxId,
      notes: data.notes,
      hasAccount: data.hasAccount || false,
      retailerId: data.retailerId,
      accountCreatedAt: data.accountCreatedAt ? toDate(data.accountCreatedAt) : undefined,
      createdAt: toDate(data.createdAt),
      updatedAt: data.updatedAt ? toDate(data.updatedAt) : undefined
    } as DirectoryPartner;
  });
};

// Get single directory partner
export const getDirectoryPartner = async (id: string): Promise<DirectoryPartner | null> => {
  const docRef = doc(db, 'retailerDirectory', id);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;

  const data = docSnap.data();
  return {
    id: docSnap.id,
    name: data.name,
    address: data.address,
    city: data.city,
    province: data.province,
    state: data.state,
    country: data.country,
    contactName: data.contactName,
    email: data.email,
    phone: data.phone,
    taxId: data.taxId,
    notes: data.notes,
    hasAccount: data.hasAccount || false,
    retailerId: data.retailerId,
    accountCreatedAt: data.accountCreatedAt ? toDate(data.accountCreatedAt) : undefined,
    createdAt: toDate(data.createdAt),
    updatedAt: data.updatedAt ? toDate(data.updatedAt) : undefined
  } as DirectoryPartner;
};

// Add new directory partner
export const addDirectoryPartner = async (
  data: Omit<DirectoryPartner, 'id' | 'hasAccount' | 'createdAt'>
): Promise<string> => {
  const partnerData = {
    ...data,
    hasAccount: false,
    createdAt: serverTimestamp()
  };
  const docRef = await addDoc(collection(db, 'retailerDirectory'), partnerData);
  return docRef.id;
};

// Update directory partner
export const updateDirectoryPartner = async (
  id: string,
  data: Partial<Omit<DirectoryPartner, 'id' | 'createdAt'>>
): Promise<void> => {
  await updateDoc(doc(db, 'retailerDirectory', id), {
    ...data,
    updatedAt: serverTimestamp()
  });
};

// Delete directory partner
export const deleteDirectoryPartner = async (id: string): Promise<void> => {
  await deleteDoc(doc(db, 'retailerDirectory', id));
};

// Mark partner as having an account created
export const markPartnerAccountCreated = async (
  partnerId: string,
  retailerId: string
): Promise<void> => {
  await updateDoc(doc(db, 'retailerDirectory', partnerId), {
    hasAccount: true,
    retailerId,
    accountCreatedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

// Batch import partners from JSON data
export interface ImportPartner {
  name: string;
  address?: string;
  city: string;
  province?: string;
  state?: string;
  country: string;
}

export const importPartnersFromJson = async (partners: ImportPartner[]): Promise<number> => {
  const batch = writeBatch(db);
  let count = 0;

  for (const partner of partners) {
    const docRef = doc(collection(db, 'retailerDirectory'));
    batch.set(docRef, {
      name: partner.name,
      address: partner.address || null,
      city: partner.city,
      province: partner.province || null,
      state: partner.state || null,
      country: partner.country,
      hasAccount: false,
      createdAt: serverTimestamp()
    });
    count++;
  }

  await batch.commit();
  return count;
};
